import { syncHebrewInputDirection } from './hebrew-input-direction.js';
import { isKioskVirtualKeyboardEnabled } from './kiosk-config.js';
import { keyboardEngine, normalizeInputElementForVirtualKeyboard } from './keyboard/keyboard-engine.js';
import {
    NUMERIC_DOT_LAYOUT,
    NUMERIC_PHONE_LAYOUT,
    NUMERIC_SPACE_LAYOUT,
    SIGNED_DECIMAL_LAYOUT,
    ZIP_LAYOUT,
    EMAIL_LAYOUT,
    TEXT_LAYOUT,
    MULTILINE_TEXT_LAYOUT,
    SELECT2_SEARCH_LAYOUT,
    HEBREW_KEYBOARD_LAYOUT,
    HEBREW_PHYSICAL_KEYS
} from './keyboard/keyboard-layouts.js';

export { normalizeInputElementForVirtualKeyboard };

function readPinMax() {
    const el = document.getElementById('kiosk-pin-config');
    if (!el) {
        return 10;
    }
    const config = JSON.parse(el.textContent);
    const pinMax = Number(config.pinMax);
    return Number.isFinite(pinMax) && pinMax > 0 ? pinMax : 10;
}

function chainCallback(base, extra) {
    if (typeof extra !== 'function') {
        return base;
    }
    if (typeof base !== 'function') {
        return extra;
    }
    return function chained(...args) {
        base(...args);
        return extra(...args);
    };
}

function markUserActive() {
    window.user_active = true;
    window.last_activity = Date.now();
}

function applyCreditCardSpacing(fullValue, keyboardState) {
    if (fullValue.length === 0) {
        return;
    }
    const isAmex = Number(fullValue.charAt(0)) === 3;
    const isBackspace = keyboardState.lastKey === 'bksp';
    const spaceLengths = isAmex ? [4, 11] : [4, 9, 14];
    const backspaceLengths = isAmex ? [5, 12] : [5, 10, 13];
    const lengths = isBackspace ? backspaceLengths : spaceLengths;
    if (lengths.includes(fullValue.length)) {
        keyboardEngine.insertText(isBackspace ? '' : ' ');
    }
}

const hebrewKeyboardOptions = {
    customLayout: HEBREW_KEYBOARD_LAYOUT,
    physicalKeys: HEBREW_PHYSICAL_KEYS,
    dismissLabel: 'Cancel',
    restoreOnDismiss: true,
    change: markUserActive
};

let hebrewKeyboardButtonsBound = false;

function isHebrewNameInput(target) {
    return target instanceof HTMLInputElement
        && target.matches('input[lang="he"], .hebrew-keyboard-field input');
}

function syncHebrewNameInputs(root) {
    if (!root || typeof root.querySelectorAll !== 'function') {
        return;
    }
    root.querySelectorAll('input[lang="he"], .hebrew-keyboard-field input').forEach((input) => {
        syncHebrewInputDirection(input);
    });
}

function inputForHebrewKeyboardButton(button) {
    const field = button.closest('.hebrew-keyboard-field');
    const input = field ? field.querySelector('input') : null;
    if (!input) {
        throw new Error('hebrew keyboard button requires an input');
    }
    return input;
}

function bindHebrewFieldDismiss(input) {
    if (input.dataset.hebrewKeyboardDismiss === '1') {
        return;
    }
    input.dataset.hebrewKeyboardDismiss = '1';
    input.addEventListener('focusout', () => {
        setTimeout(() => {
            const active = document.activeElement;
            const tray = document.getElementById('app-keyboard-tray');
            if (active === input || (tray && tray.contains(active))) {
                return;
            }
            if (keyboardEngine.state && keyboardEngine.state.el === input) {
                keyboardEngine.hide();
            }
        }, 0);
    });
}

export function openHebrewKeyboard(input) {
    if (!input || input.tagName !== 'INPUT') {
        throw new Error('openHebrewKeyboard: input is required');
    }
    const options = input.maxLength > 0
        ? { ...hebrewKeyboardOptions, maxLength: input.maxLength }
        : { ...hebrewKeyboardOptions };
    if (!isKioskVirtualKeyboardEnabled()) {
        options.accept = 'ok';
    }
    options.beforeClose = () => syncHebrewInputDirection(input);
    input.dir = 'rtl';
    bindHebrewFieldDismiss(input);
    input.focus();
    keyboardEngine.show(input, options);
}

export function bindHebrewKeyboardButtons() {
    if (hebrewKeyboardButtonsBound) {
        return;
    }
    hebrewKeyboardButtonsBound = true;
    document.addEventListener('input', (event) => {
        const input = event.target;
        if (!isHebrewNameInput(input)) {
            return;
        }
        if (keyboardEngine.state && keyboardEngine.state.el === input) {
            input.dir = 'rtl';
            return;
        }
        syncHebrewInputDirection(input);
    });
    document.addEventListener('mousedown', (event) => {
        if (event.target.closest('[data-hebrew-keyboard]')) {
            event.preventDefault();
        }
    });
    document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-hebrew-keyboard]');
        if (!button) {
            return;
        }
        event.preventDefault();
        openHebrewKeyboard(inputForHebrewKeyboardButton(button));
    });
    syncHebrewNameInputs(document);
    const observer = new MutationObserver((records) => {
        records.forEach((record) => {
            record.addedNodes.forEach((node) => {
                if (node.nodeType !== Node.ELEMENT_NODE) {
                    return;
                }
                if (isHebrewNameInput(node)) {
                    syncHebrewInputDirection(node);
                }
                syncHebrewNameInputs(node);
            });
        });
    });
    observer.observe(document.body, { childList: true, subtree: true });
}

const textKeyboardOptions = {
    display: { alt: 'עב', normal: 'EN' },
    customLayout: TEXT_LAYOUT,
    stickyShift: false,
    autoShift: true,
    change: markUserActive
};

const multilineTextKeyboardOptions = { ...textKeyboardOptions, customLayout: MULTILINE_TEXT_LAYOUT };

const signedDecimalKeyboardOptions = {
    numeric: true,
    customLayout: SIGNED_DECIMAL_LAYOUT,
    change: markUserActive
};

function fieldKeyboardOptions(el) {
    if (el instanceof HTMLTextAreaElement) {
        return multilineTextKeyboardOptions;
    }
    if (['numeric', 'decimal'].includes(el.inputMode)) {
        return signedDecimalKeyboardOptions;
    }
    return textKeyboardOptions;
}

export function attachFieldVirtualKeyboard(el) {
    normalizeInputElementForVirtualKeyboard(el);
    keyboardEngine.attach(el, fieldKeyboardOptions(el));
}

const amountKeyboardOptions = {
    numeric: true,
    customLayout: NUMERIC_DOT_LAYOUT,
    change(event, state, el) {
        markUserActive();
        const match = el.value.match(/(\d{1,5})(\.\d{0,2})?/);
        if (match) {
            el.value = match.slice(1).join('');
            el.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }
};

export function getAmountVirtualKeyboardOptions(overrides) {
    const { visible, beforeClose, change, keyboardChange, ...rest } = overrides || {};
    return {
        ...amountKeyboardOptions,
        ...rest,
        visible: chainCallback(amountKeyboardOptions.visible, visible),
        beforeClose: chainCallback(amountKeyboardOptions.beforeClose, beforeClose),
        change: chainCallback(amountKeyboardOptions.change, change),
        keyboardChange: chainCallback(amountKeyboardOptions.keyboardChange, keyboardChange)
    };
}

export function attachAmountVirtualKeyboard(targets, overrides) {
    if (!isKioskVirtualKeyboardEnabled()) {
        return targets && targets.jquery ? targets : $(targets);
    }
    const $targets = targets && targets.jquery ? targets : $(targets);
    if (!$targets.length) {
        return $targets;
    }
    $targets.each(function attachOne() {
        normalizeInputElementForVirtualKeyboard(this);
    });
    $targets.keyboard(getAmountVirtualKeyboardOptions(overrides));
    return $targets;
}

function attachKeyboard(selector, overrides, numeric) {
    if (!isKioskVirtualKeyboardEnabled()) {
        return $(selector);
    }
    const $targets = $(selector);
    $targets.each(function attachOne() {
        normalizeInputElementForVirtualKeyboard(this);
    });
    $targets.keyboard({ numeric: Boolean(numeric), ...overrides });
    return $targets;
}

export function attachKeyboardForAliyahPledgeNewRows($scope) {
    if (!$scope || !$scope.length) {
        throw new Error('attachKeyboardForAliyahPledgeNewRows: $scope is required');
    }
    if (!isKioskVirtualKeyboardEnabled()) {
        return;
    }
    attachAmountVirtualKeyboard($scope.find('.aliyah_pledge_amount_add, .kibbud_pledge_amount'));
    const $names = $scope.find('.kibbud_name');
    $names.each(function attachOne() {
        normalizeInputElementForVirtualKeyboard(this);
    });
    $names.keyboard(textKeyboardOptions);
}

export function attachKeyboardsToContainer($container) {
    if (!$container || !$container.length) {
        throw new Error('attachKeyboardsToContainer: $container is required');
    }
    if (!isKioskVirtualKeyboardEnabled()) {
        return;
    }
    attachAmountVirtualKeyboard($container.find('.form_amount_value'));
    const $labels = $container.find('.form_amount_label, .form_amount_labels');
    $labels.each(function attachOne() {
        normalizeInputElementForVirtualKeyboard(this);
    });
    $labels.keyboard(textKeyboardOptions);
}

export function getSelect2SearchFieldKeyboardOptions() {
    return {
        display: { alt: 'עב', normal: 'EN' },
        customLayout: SELECT2_SEARCH_LAYOUT,
        stickyShift: false,
        autoShift: true,
        change() {
            $('.select2-search__field').trigger('input');
        }
    };
}

export function initSelect2VirtualKeyboard() {
    if (!isKioskVirtualKeyboardEnabled()) {
        return;
    }
    $(document)
        .off('select2:open.virtualKeyboardSelect2')
        .on('select2:open.virtualKeyboardSelect2', () => {
            const field = document.querySelector('.select2-search__field');
            if (!field) {
                return;
            }
            const options = getSelect2SearchFieldKeyboardOptions();
            keyboardEngine.attach(field, options);
            keyboardEngine.show(field, options);
            field.focus();
        });
}

export function initVirtualKeyboard() {
    if (!isKioskVirtualKeyboardEnabled()) {
        return;
    }
    const pinMax = readPinMax();

    const $textTargets = $('.mass_communication, .show_virtual_keyboard, #name, #comments, #donor_comments, #first_name, #last_name, #address, #city, #custom_description, #custom_desc, #edit_card_name, #donor_name, #edit_first_name, #edit_last_name, #edit_address, #edit_city, #amount_label, .form_amount_label, .form_amount_labels, #plan_name, #pledge_description, #camp_title, #email_receipt, #email_statement, #hebrew_name, #father_hebrew_name, #notes, #edit_hebrew_name, #edit_father_hebrew_name, #edit_notes, #parsha_name, #member_list_search_input, #mgr_set_paid_notes, #donor_pay_comments, #camp_link_for_campaign');
    $textTargets.each(function attachOne() {
        normalizeInputElementForVirtualKeyboard(this);
    });
    $textTargets.keyboard(textKeyboardOptions);

    attachAmountVirtualKeyboard('.amount_value, .pledge_amt_numeric, #donation_amount, #amount, #amount_value, #elevation, #on_amount, #cover_surcharge_percent, .pledge-amount, .form_amount_value, .open-payment-amount-input, #edit_amount');

    attachKeyboard('#latitude, #longitude', { customLayout: SIGNED_DECIMAL_LAYOUT }, true);

    attachKeyboard('#phone, #edit_phone, #sms_test_input, #text_resend_receipt', {
        customLayout: NUMERIC_PHONE_LAYOUT,
        maxLength: 12,
        change(event, state, el) {
            markUserActive();
            if (state.lastKey !== 'bksp' && (el.value.length === 3 || el.value.length === 7)) {
                keyboardEngine.insertText('-');
            }
        }
    }, true);

    attachKeyboard('#rows, #columns', { customLayout: NUMERIC_SPACE_LAYOUT, maxLength: 5 }, true);

    attachKeyboard('.duration-control__number', {
        customLayout: NUMERIC_SPACE_LAYOUT,
        maxLength: 2,
        change: markUserActive
    }, true);

    attachKeyboard('#zip_code, #zip, #edit_zip, #avs_zip, #avs_zip_edit, .vk-card-zip', {
        customLayout: ZIP_LAYOUT,
        display: { alt: 'ABC', normal: '123' },
        maxLength: 10,
        change: markUserActive
    }, true);

    attachKeyboard('#card, .vk-card-number', {
        customLayout: NUMERIC_SPACE_LAYOUT,
        maxLength: 19,
        change(event, state, el) {
            markUserActive();
            applyCreditCardSpacing(el.value, state);
        }
    }, true);

    attachKeyboard('#pin, #confirm_pin, #otp', {
        customLayout: NUMERIC_SPACE_LAYOUT,
        maxLength: pinMax,
        maskPreview: true,
        change(event, state, el) {
            markUserActive();
            if (typeof pinValidation === 'function') {
                pinValidation(el.value);
            }
        }
    }, true);

    attachKeyboard('#expiry_month, #expiry_year, .vk-card-expiry', { customLayout: NUMERIC_SPACE_LAYOUT, maxLength: 2, change: markUserActive }, true);

    attachKeyboard('#cvv2, .vk-card-cvv2', { customLayout: NUMERIC_SPACE_LAYOUT, maxLength: 4, maskPreview: true, change: markUserActive }, true);

    attachKeyboard('#email, #donor_email, #edit_email, #email_test_input, #email_resend_receipt', {
        customLayout: EMAIL_LAYOUT,
        stickyShift: false,
        change: markUserActive
    });
}
